import { defineStore } from "pinia";
import type { IJournal, IResearchTopic } from "~/types";

import { type IColor } from "@frontiers-vue3/public-pages";
import type { IContentfulJournal } from "~/types/contentful/IContentfulJournal";
import { useJournalPagesService } from "~/composables/useJournalPagesService";
import { useMongoDbService } from "~/composables/useMongoDbService";
import { useProxyService } from "~/composables/useProxyService";
import type { IAppMainStoreState } from "~/types/store";
import { TopicStageEnum } from "~/enums";
import { SearchDataModel } from "~/types/models/SearchData.model";
import { ArticleSortOptionEnum } from "~/enums/ArticleSortOptionEnum";
import type { IChiefEditorsResponse } from "~/types/IChiefEditorsResponse";
import { buildClib3Breadcrumbs } from "~/helpers/buildClib3Breadcrumbs";

export const useMain = defineStore("main", {
  state: (): IAppMainStoreState => {
    return {
      currentResearchTopic: {} as IResearchTopic,
      primaryJournal: {} as IContentfulJournal,
      participatingJournals: [],
      totalArticles: 0,
      totalAuthors: 0,
      articlesForOverview: [],
      breadCrumbItems: [],
      rtSponsorship: undefined,
      specialtyChiefEditors: []
    };
  },
  getters: {
    getPrimaryJournal(): IJournal | undefined {
      const primaryJournal = this.currentResearchTopic.journals?.find(
        (j) => j.isPrimary
      );
      if (primaryJournal) {
        return primaryJournal;
      } else if (
        !primaryJournal &&
        this.currentResearchTopic.journals.length > 0
      ) {
        return this.currentResearchTopic.journals[0];
      }
    },
    getPrimaryJournalId(): number | undefined {
      const primaryJournal = this.getPrimaryJournal;
      return primaryJournal?.id;
    },
    colorPalette(): IColor {
      return (this.primaryJournal?.palette as IColor) || "blue";
    },
    getParticipatingJournalsToSubmit(): IJournal[] {
      return this.currentResearchTopic.journals
        .filter((j) => j.isOpenForSubmission)
        .map((j) => {
          return {
            ...j,
            sections: j.sections?.filter((s) => s.isOpenForSubmission)
          };
        })
        .filter(
          (j) => j.taxonomy.find((t) => t.specialityId) || j.sections?.length
        );
    },
    getIsSubmitManuscriptEnabled(): boolean {
      const { isManuscriptButtonVisible } =
        useEnvironmentConfigStore().getConfig();
      return (
        this.getParticipatingJournalsToSubmit.length > 0 &&
        (this.currentResearchTopic.isOpenForSubmission ?? false) &&
        !this.currentResearchTopic.closedOn &&
        !!isManuscriptButtonVisible
      );
    },
    isCurrentResearchTopicOpen(): boolean {
      return (
        this.currentResearchTopic.stageId === TopicStageEnum.Open &&
        this.currentResearchTopic.isOpenForSubmission
      );
    }
  },
  actions: {
    async fetchCurrentResearchTopic(researchTopicId: number) {
      const { getTerm } = useTerminologyStore();
      const { getResearchTopicDetails, getResearchTopicSponsorshipDetails } =
        useMongoDbService();

      const [rtDetails, rtSponsor] = await Promise.all([
        getResearchTopicDetails(researchTopicId, getTerm("tenantName")),
        getResearchTopicSponsorshipDetails(researchTopicId)
      ]);

      this.currentResearchTopic = rtDetails;
      this.rtSponsorship = rtSponsor;
    },

    fetchBreadcrumbs(rtDetails: IResearchTopic) {
      const router = useRequestURL();
      const runtimeConfig = useRuntimeConfig();

      this.breadCrumbItems = buildClib3Breadcrumbs(
        rtDetails,
        `${useGetBaseUrl(router, runtimeConfig)}`
      );
    },

    async fetchContentfulJournal(journalSlug: string) {
      const { getContentfulJournal } = useJournalPagesService();
      try {
        this.primaryJournal = await getContentfulJournal(journalSlug);
      } catch (err: unknown) {
        throw Error(
          `Error getting journal info ${journalSlug}. Message: ${err instanceof Error ? err.message : err
          }`
        );
      }
    },

    async fetchParticipatingJournals() {
      const { getContentfulJournals } = useJournalPagesService();
      try {
        this.participatingJournals = await getContentfulJournals(this.currentResearchTopic.journals ?? []);
      } catch (err: unknown) {
        throw Error(
          `Error getting participating journals info. Message: ${err instanceof Error ? err.message : err
          }`
        );
      }
    },

    async fetchTotals() {
      const researchTopicId = this.currentResearchTopic.id;
      const { getTotalArticles, getTotalAuthors } = useRtApiService();

      const [totalArticles, totalAuthors] = await Promise.all([
        getTotalArticles(researchTopicId),
        getTotalAuthors(researchTopicId)
      ]);

      this.totalArticles = totalArticles;
      this.totalAuthors = totalAuthors;
    },

    async fetchArticlesForOverview() {
      const { getArticles } = useMongoDbService();
      const researchTopicId = this.currentResearchTopic.id;
      const articles = await getArticles(
        researchTopicId,
        new SearchDataModel(0, 3, ArticleSortOptionEnum.DATE)
      );

      this.articlesForOverview = articles ?? [];
    },

    async fetchSpecialtyChiefEditors() {
      const primaryJournal = this.getPrimaryJournal;

      if (!primaryJournal) return;

      const onlineSection = primaryJournal.sections?.find((s) => s.isOnline);

      const { getChiefEditorsByJournalId, getChiefEditorsBySectionId } =
        useSearchService();

      try {
        const data = onlineSection
          ? await getChiefEditorsBySectionId(
            primaryJournal.id,
            onlineSection.sectionId
          )
          : await getChiefEditorsByJournalId(primaryJournal.id);

        this.specialtyChiefEditors = data.map((r: IChiefEditorsResponse) => ({
          loopUserId: r.UserId,
          fullName: r.FullName,
          organizationName: r.Affiliation.Organization,
          organizationLocation: [r.Affiliation.City, r.Affiliation.Country]
            .filter(Boolean)
            .join(", ")
        }));
      } catch (e) {
        this.specialtyChiefEditors = [];
      }
    }
  }
});
